import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import { Box, Card, Grid, CircularProgress } from '@mui/material';
import ByzMuiDataGrid from 'src/components/core-components/mui/data-grid';
import { ByzMuiCheckBox } from 'src/components/core-components/mui/ByzMuiCheckBox';
import { useTranslate } from 'src/locales';
import { useBaseQuery } from 'src/api/core/useBaseQuery';
import dayjs from 'dayjs';

// API Response Type
interface WaitingPatientOrder {
  srdid: number;
  apdate: string;
  psid: number;
  srpsid: number;
  warehousename: string;
  stockname: string;
  ispatientdrug: string | null;
  appfrequence: number;
  dose: string;
  unitname: string;
  appunitname: string;
  unitquantity: number;
  stockunitname: string;
  dumname: string;
  usepurpose: string | null;
  reqnote: string | null;
  note: string | null;
  useasnecessary: string | null;
  isnarcotic: string | null;
  psinfo: string;
  appfrequenceinfo: string;
  stockunitvalue: string;
}

interface WaitingMenuProps {
  patientId?: string | number;
}

const WaitingMenu: React.FC<WaitingMenuProps> = ({ patientId }) => {
  const { t } = useTranslate();

  // API endpoint
  const apiEndpoint = useMemo(() => {
    if (!patientId) return '';
    return `/nurse/api/Order/GetWaitingPatientOrder?patientId=${patientId}`;
  }, [patientId]);

  // API çağrısı
  const { data: apiData, isLoading, error } = useBaseQuery<WaitingPatientOrder[]>({
    endpoint: apiEndpoint,
    queryKey: ['waiting-patient-order', patientId],
    options: {
      enabled: !!apiEndpoint,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  // API datayı grid formatına dönüştür
  const rows = useMemo(() => {
    if (!apiData || apiData.length === 0) return [];

    return apiData.map((item, index) => ({
      id: `${item.srdid}-${index}`, // Unique ID için srdid ve index kombinasyonu
      orderTime: item.apdate ? dayjs(item.apdate).format('DD.MM.YYYY HH:mm') : '-',
      status: item.psinfo || '-',
      medicine: item.stockname || '-',
      hyi: item.ispatientdrug === '1' || String(item.ispatientdrug) === '1',
      frequency: item.appfrequenceinfo || '-',
      dose: item.stockunitvalue || '-',
      usageForm: item.dumname || '-',
      usagePurpose: item.usepurpose || '-',
      requestNote: item.reqnote || '-',
      pharmacyNote: item.note || '-',
      lh: item.useasnecessary === '1' || String(item.useasnecessary) === '1',
      narcotic: item.isnarcotic === '1' || String(item.isnarcotic) === '1',
      rawData: item, // Orijinal veriyi sakla
      srdid: item.srdid, // Orijinal srdid'yi de sakla (ihtiyaç olursa)
    }));
  }, [apiData]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'orderTime',
        headerName: t('nurse.order-medicine.waiting-menu.columns.order-time'),
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'status',
        headerName: t('nurse.order-medicine.waiting-menu.columns.status'),
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'medicine',
        headerName: t('nurse.order-medicine.waiting-menu.columns.medicine'),
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Box sx={{ fontWeight: 500, letterSpacing: '0.17px', lineHeight: '143%' }}>
            {params.value}
          </Box>
        ),
      },
      {
        field: 'hyi',
        headerName: t('nurse.order-medicine.waiting-menu.columns.hyi'),
        flex: 0.5,
        minWidth: 80,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ByzMuiCheckBox checked={params.value} disabled />
          </Box>
        ),
      },
      {
        field: 'frequency',
        headerName: t('nurse.order-medicine.waiting-menu.columns.frequency-dose'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'dose',
        headerName: t('nurse.order-medicine.waiting-menu.columns.drug-dose'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'usageForm',
        headerName: t('nurse.order-medicine.waiting-menu.columns.usage-form'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'usagePurpose',
        headerName: t('nurse.order-medicine.waiting-menu.columns.usage-purpose'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'requestNote',
        headerName: t('nurse.order-medicine.waiting-menu.columns.request-note'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'pharmacyNote',
        headerName: t('nurse.order-medicine.waiting-menu.columns.pharmacy-note'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'lh',
        headerName: t('nurse.order-medicine.waiting-menu.columns.lh'),
        flex: 0.5,
        minWidth: 60,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {params.value && (
              <Box
                sx={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '20px',
                  backgroundColor: 'rgba(0, 0, 0, 0.38)',
                }}
              />
            )}
          </Box>
        ),
      },
      {
        field: 'narcotic',
        headerName: t('nurse.order-medicine.waiting-menu.columns.narcotic'),
        flex: 0.5,
        minWidth: 80,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {params.value && (
              <Box
                sx={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '20px',
                  backgroundColor: 'rgba(0, 0, 0, 0.38)',
                }}
              />
            )}
          </Box>
        ),
      },
    ],
    [t]
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#2979C3' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          color: 'error.main',
        }}
      >
        {t('nurse.order-medicine.waiting-menu.error')}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Card sx={{ marginBottom: '5px' }}>
        <Grid
          container
          sx={{ margin: '8px', p: 2, borderBottom: '1px solid #E0E0E0' }}
        >
          <span style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
            {t('nurse.patient-list.detail-grid.treatment-applications')}
          </span>
        </Grid>
        <ByzMuiDataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          whiteGrid={true}
          sx={{
            marginLeft: '8px',
            marginRight: '8px',
          }}
        />
      </Card>
    </Box>
  );
};

export default WaitingMenu;

